import React, { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import DataTable from "aldoo-ra/DataTable"
import { api, generateCRUDInterface } from "aldoo-ra/API"
import { SupportedTypes } from "../controller/defines"
import { rendersForType } from "../controller/item-renders"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import FilterPanel from "./FilterPanel"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"
import moment from "moment"

const UpdateDialog = ({
  isOpen,
  onClose,
  controller,
  initialData = null,
  onSave,
}) => {
  const [selectedType, setSelectedType] = useState(null)
  const [queryMatch, setQueryMatch] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [dateFilter, setDateFilter] = useState({ type: "preset", value: null }) // Lifetime by default

  // Initialize the CRUD API when type changes
  const crudAPI = useMemo(() => {
    if (!selectedType?.value) return null

    //this is a File from the CMS
    if (selectedType.dataType === "file") {
      const cmsAPI = api("cms")
      return {
        Get: async (query) => {
          return await cmsAPI({
            file_list: {
              ...query,
            },
          })
        },
      }
    }

    const baseAPI = api("crud")
    return generateCRUDInterface(baseAPI, selectedType.value)
  }, [selectedType])

  useEffect(() => {
    if (initialData) {
      setSelectedType(initialData.type)
      // Convert query match object to array of key/value pairs
      const matchPairs = Object.entries(initialData.query.match || {}).map(
        ([key, value]) => {
          // Determine operator and type based on value
          let operator = "$eq"
          let type = "text"
          let displayValue = value

          if (typeof value === "object" && value !== null) {
            // Check for MongoDB operators
            if (value.$regex) {
              operator = "$regex"
              displayValue = value.$regex
            } else if (value.$ne !== undefined) {
              operator = "$ne"
              displayValue = value.$ne
            } else if (value.$gt !== undefined) {
              operator = "$gt"
              displayValue = value.$gt
              type = "number"
            } else if (value.$gte !== undefined) {
              operator = "$gte"
              displayValue = value.$gte
              type = "number"
            } else if (value.$lt !== undefined) {
              operator = "$lt"
              displayValue = value.$lt
              type = "number"
            } else if (value.$lte !== undefined) {
              operator = "$lte"
              displayValue = value.$lte
              type = "number"
            } else if (value.$in !== undefined) {
              operator = "$in"
              displayValue = Array.isArray(value.$in) ? value.$in.join(", ") : value.$in
            } else if (value.$nin !== undefined) {
              operator = "$nin"
              displayValue = Array.isArray(value.$nin) ? value.$nin.join(", ") : value.$nin
            } else {
              // Complex object, stringify it
              displayValue = JSON.stringify(value)
            }
          } else if (typeof value === "boolean") {
            type = "boolean"
            displayValue = value.toString()
          } else if (typeof value === "number") {
            type = "number"
            displayValue = value.toString()
          } else {
            displayValue = value.toString()
          }

          return {
            key,
            value: displayValue,
            operator,
            type
          }
        }
      )
      setQueryMatch(matchPairs)
    }
  }, [initialData])

  // Handle date filter changes
  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter)
    setRefreshKey((prev) => prev + 1) // Refresh table when date filter changes
  }

  // Check if selected type supports date filtering (has createdAt field)
  const supportsDateFiltering = () => {
    if (!selectedType) return false
    // CMS types and most database models have createdAt field
    return ["CMSContent", "CMSType", "StorePrice", "StoreProduct", "User", "UserDevice", "Localization"].includes(selectedType.value)
  }

  // Convert query object from ObjectEditor format to MongoDB query format
  const buildQuery = () => {
    const matchObject = queryMatch.reduce((acc, { key, value, operator = "$eq", type = "text" }) => {
      if (!key || (value === "" && value !== 0 && value !== false)) return acc

      try {
        // Parse value if it looks like JSON
        let parsedValue = value
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            parsedValue = JSON.parse(value)
          } catch {
            parsedValue = value
          }
        }

        // Convert value based on type
        if (type === "number" && typeof parsedValue === "string") {
          parsedValue = Number(parsedValue)
        } else if (type === "boolean" && typeof parsedValue === "string") {
          parsedValue = parsedValue.toLowerCase() === "true"
        }

        // Handle regex (contains) operator
        if (operator === "$regex") {
          acc[key] = { $regex: parsedValue, $options: "i" }
        }
        // Handle "in list" operator
        else if (operator === "$in" || operator === "$nin") {
          // Convert comma-separated string to array
          const arrayValue = Array.isArray(parsedValue)
            ? parsedValue
            : typeof parsedValue === "string"
            ? parsedValue.split(",").map(v => v.trim())
            : [parsedValue]
          acc[key] = { [operator]: arrayValue }
        }
        // Handle equals operator - direct assignment
        else if (operator === "$eq") {
          acc[key] = parsedValue
        }
        // Handle all other operators
        else {
          acc[key] = { [operator]: parsedValue }
        }
      } catch (error) {
        console.error(`Error processing filter ${key}:`, error)
      }
      return acc
    }, {})

    // Add date filtering if supported and active
    if (supportsDateFiltering() && dateFilter) {
      if (dateFilter.type === "preset" && dateFilter.value) {
        // Preset date (e.g., last 24h, last 7d)
        const { days } = dateFilter.value
        if (days) {
          const cutoffDate = moment().subtract(days, "days").utc().toISOString()
          matchObject.createdAt = { $gte: cutoffDate }
        }
      } else if (dateFilter.type === "custom" && dateFilter.range) {
        // Custom date range
        const { startDate, endDate } = dateFilter.range
        if (startDate && endDate) {
          const adjustedEndDate = moment(endDate).utc().endOf("day").toISOString()
          const adjustedStartDate = moment(startDate).utc().startOf("day").toISOString()
          matchObject.createdAt = {
            $gte: adjustedStartDate,
            $lte: adjustedEndDate,
          }
        }
      }
    }

    return { match: matchObject }
  }

  const handleSave = async () => {
    if (!selectedType || !crudAPI) return

    try {
      // Execute query once to get total count
      const queryResult = await crudAPI.Get({
        match: buildQuery().match,
        page: 1,
      })

      const updateConfig = {
        ...(initialData?.id ? { id: initialData.id } : {}),
        type: selectedType,
        query: buildQuery(),
        details: `${selectedType.label} - ${queryResult.totalItems} items`,
        itemCount: queryResult.totalItems,
        include: [],
        exclude: [],
      }

      if (initialData?.id) {
        controller.add(updateConfig)
      } else {
        const newUpdate = controller.create()
        controller.add({
          ...newUpdate,
          ...updateConfig,
        })
      }

      onSave && onSave()
      onClose()
    } catch (error) {
      console.error("Error saving configuration:", error)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className=" md:absolute w-full sm:w-[500px] md:w-[95%] lg:max-w-[1500px] h-full md:h-auto md:min-h-[85%]"
      headerContent={null}
    >
      <div className="p-6">
        {/* Data Table with all controls */}
        <div className="min-h-[500px]">
          <DataTable
            localUserPreferencesKey="admin.updates.addDialog"
            variant="admin_browser"
            title={
              <div className="flex items-center gap-2">
                <ArchiveBoxIcon className="w-5 h-5" />
                Update Package Contents
              </div>
            }
            apiMethod={selectedType && crudAPI ? crudAPI.Get : () => Promise.resolve({ data: [], totalItems: 0 })}
            preferredHeight="70vh"
            itemHeight="h-[50px]"
            renders={selectedType ? rendersForType(selectedType.value, buildQuery().match) : []}
            refreshKey={refreshKey}
            searchField={selectedType?.searchFields || "name title"}
            searchObject={{}}
            requestFields={{
              match: {
                ...buildQuery().match,
                deleted: { $ne: true },
              },
            }}
            sort={{ createdAt: -1 }}
            styleOverrides={{
              container: "relative flex flex-col h-full",
              header: "flex flex-row items-center gap-4 mb-2",
              title: "absolute -top-7 text-admin_text text-xl font-semibold",
              searchInput: "w-full max-w-[300px] mt-3 mr-auto",
            }}
            headerControls={
              <div className="w-full flex flex-row items-end gap-3 justify-end mt-3 ml-auto">
                {/* Data Type Dropdown */}
                <div className="flex flex-col -mt-6">
                  <Typography
                    variant="admin_txt_accent"
                    className="text-admin_text mb-0.5"
                  >
                    Data Type
                  </Typography>

                  <DropDownSelect
                    variant="admin"
                    searchEnabled
                    options={SupportedTypes}
                    value={selectedType}
                    placeholder="Select data type..."
                    fixedWidth="235px"
                    onChange={(value) => {
                      setSelectedType(value[0])
                      setQueryMatch([]) // Reset query when type changes
                      setDateFilter({ type: "preset", value: null }) // Reset date filter
                      setRefreshKey((prev) => prev + 1)
                    }}
                    valueField="value"
                  />
                </div>

                {/* Date Filter */}
                <div className="flex items-center w-48 -mt-6">
                  <DateFilterDropdown
                    onChange={handleDateFilterChange}
                    variant="admin"
                    value={dateFilter}
                    className="w-full"
                  />
                </div>

                {/* Custom Filter Panel */}
                <FilterPanel
                  value={queryMatch}
                  onChange={setQueryMatch}
                  onApply={(value) => {
                    setQueryMatch(value)
                    setRefreshKey((prev) => prev + 1)
                  }}
                  onClear={() => {
                    setQueryMatch([])
                    setRefreshKey((prev) => prev + 1)
                  }}
                  disabled={!selectedType}
                  selectedModelType={selectedType}
                />

                {/* Add to Package Button */}
                <Button 
                  variant="admin_primary" 
                  onClick={handleSave} 
                  disabled={!selectedType}
                  className="whitespace-nowrap"
                >
                  Add to Package
                </Button>
              </div>
            }
            noData={
              <div className="text-center py-8">
                {!selectedType ? (
                  <div>
                    <Typography variant="admin_body_dim">
                      Select a data type to see available content
                    </Typography>

                    <Typography variant="admin_body_dim">
                      Use the "Data Type" dropdown in the header controls above
                    </Typography>
                  </div>
                ) : (
                  <div>
                    <Typography variant="admin_body_dim">
                      No results found for the current filters
                    </Typography>

                    <Typography variant="admin_body_dim">
                      Try adjusting your date range or match conditions
                    </Typography>
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

export default UpdateDialog
